import EventContentState from "../../../components/EventContentState";
import {eventLinkProperties} from '../../../services/embedded-events';
import { eventMetadata } from "../../../services/eventMetadata";
import React from "react";
import Link from "next/link";
import Meta from "../Meta";
import Icon from "../components/Icon";
import Button from "../components/Button";
import EventMultiSession from "../../Travel/Event/EventMultiSession"; // réutilisé tel quel (styles inline scoped)
import Loader from "../loader";
import { applyLang } from "../../../i18n";
import { useEvents } from "../../../services/EventService";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { resolveImage, resolveUrl } from "../utils";
import { isExternalUrl, buildLinkHref } from "../../../components/normalizeMenuUrl";
import AnnualEventPage from "../../../components/AnnualEventPage";
import EventAiContent from "../../../components/EventAiContent";
import { formatEventDate } from "../../../components/eventDate";

const styles = `
.t26-event-page {
  padding: clamp(24px, 4vw, 56px) clamp(16px, 4vw, 32px) clamp(40px, 6vw, 80px);
  max-width: var(--container);
  margin-inline: auto;
}
.t26-event-page__crumbs {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600;
  letter-spacing: .12em; text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 18px;
}
.theme-2026--embed .t26-event-page__crumbs {
  display: none;
}
.t26-event-page__crumbs a {
  color: inherit;
  text-decoration: none;
  transition: color .15s ease;
}
.t26-event-page__crumbs a:hover { color: var(--primary); }
.t26-event-page__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  gap: clamp(28px, 4vw, 56px);
  align-items: start;
}
@media (max-width: 800px) {
  .t26-event-page__grid { grid-template-columns: 1fr; gap: 24px; }
}
.t26-event-page__poster {
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: clamp(18px, 2vw, 24px);
  overflow: hidden;
  background: color-mix(in oklab, var(--primary) 14%, var(--bg-2));
  box-shadow: var(--shadow-md);
}
.t26-event-page__poster img { width: 100%; height: 100%; object-fit: cover; }
.t26-event-page__date {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 14px;
  background: color-mix(in oklab, var(--primary) 10%, var(--surface));
  color: var(--primary);
  border-radius: var(--r-pill);
  font-size: 12px; font-weight: 700;
  letter-spacing: .12em; text-transform: uppercase;
  margin-bottom: 16px;
}
.t26-event-page__title {
  font-family: var(--font-display);
  margin: 0;
  font-size: clamp(28px, 4.4vw, 48px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -.02em;
  color: var(--ink);
  text-wrap: balance;
}
.t26-event-page__address {
  margin-top: 18px;
  display: inline-flex; align-items: flex-start; gap: 8px;
  color: var(--ink-3);
  font-size: 15px;
}
.t26-event-page__content {
  margin-top: 24px;
  font-size: 16px; line-height: 1.7;
  color: var(--ink-2);
}
.t26-event-page__links {
  margin-top: 32px;
  padding: 20px;
  background: var(--bg-2);
  border-radius: var(--r-lg);
  display: flex; flex-direction: column; gap: 10px;
}
.t26-event-page__link {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px;
  background: var(--surface);
  border-radius: var(--r-md);
  text-decoration: none;
  color: var(--ink);
  border: 1px solid var(--line);
  transition: transform .15s, border-color .15s;
}
.t26-event-page__link:hover {
  transform: translateY(-1px);
  border-color: var(--primary);
}
.t26-event-page__link b {
  font-weight: 600;
  color: var(--primary);
}
.t26-event-page__custom-content {
  margin-top: clamp(28px, 5vw, 56px);
}
`;

export default function Event({ context, loading }) {
    const { org, langValue } = context;
    // Événements fetchés CÔTÉ CLIENT via /v1/events (modèle galeries/actualités).
    const { events, loading: eventsLoading, error: eventsError, retry } = useEvents(context);
    // L'événement (affiche ou PE) est résolu dans la liste unifiée.
    const eventKey = context.page?.eventId || context.route[1];
    const rawEvent = (events || []).find(i => i.id === eventKey || i._id === eventKey || i.urlSlug === eventKey);
    const event = rawEvent ? applyLang(rawEvent, context.lang) : null;
    const isMultiSession = !!event?.isMultiSession;
    const pageMeta = eventMetadata(context);

    // Tant que le fetch client est en cours, on montre le Loader (sinon on
    // afficherait "introuvable" avant l'arrivée des données).
    if (loading || eventsLoading) {
        return (
            <>
                <Meta context={context} title={event?.name || pageMeta.title || t`Événement`} description={event?.description || pageMeta.description} image={event?.image ? resolveImage(event.image) : pageMeta.image} />
                <Loader />
            </>
        );
    }

    if (eventsError) return <EventContentState error retry={retry}/>;

    if (!event) {
        return (
            <>
                <Meta context={context} title={t`Événement introuvable`} noindex />
                <div style={{ padding: 60, textAlign: "center", color: "var(--ink-3)" }}>
                    <Trans>Cet événement n'a pas été trouvé.</Trans>
                </div>
            </>
        );
    }

    if (isMultiSession) {
        return (
            <>
                <Meta context={context} title={event.name} description={event.description} image={event.image ? resolveImage(event.image) : null} />
                <EventMultiSession context={context} />
            </>
        );
    }

    if (event.annualEventPage) return <>
        <Meta context={context} title={event.name} description={event.description} image={event.image ? resolveImage(event.image) : null}/>
        <AnnualEventPage event={event} context={context}/>
    </>;

    // Période quand le début et la fin ne tombent pas le même jour (PE uniquement,
    // cf. components/eventDate.js).
    const eventDateText = formatEventDate(event, langValue);

    const hasContent = !!(event.content || event.description);
    const hasLinks = Array.isArray(event.links) && event.links.length > 0;

    return (
        <>
            <Meta context={context} title={event.name} description={event.description} image={event.image ? resolveImage(event.image) : null} />
            <style dangerouslySetInnerHTML={{ __html: styles }} />

            <div className="t26-event-page">
                <div className="t26-event-page__crumbs">
                    <Link href={org.BASE_WEB_URL} legacyBehavior><a><Trans>Accueil</Trans></a></Link>
                    <Icon name="chevronRight" size={11} className="t26-flip-rtl" />
                    <Link href={`${org.BASE_WEB_URL}/evenements`} legacyBehavior><a><Trans>Événements</Trans></a></Link>
                </div>

                <div className="t26-event-page__grid">
                    {event.image && (
                        <div className="t26-event-page__poster">
                            <img src={resolveImage(event.image)} alt={event.name} />
                        </div>
                    )}
                    <div>
                        {eventDateText && (
                            <div className="t26-event-page__date">
                                <Icon name="phone" size={11} style={{ opacity: 0 }} />
                                {eventDateText}
                            </div>
                        )}
                        <h1 className="t26-event-page__title">{event.name}</h1>

                        {event.address?.address && (
                            <div className="t26-event-page__address">
                                <Icon name="mapPin" size={16} />
                                <span>{event.address.address}</span>
                            </div>
                        )}

                        {hasContent && (
                            <div className="t26-event-page__content" dangerouslySetInnerHTML={{ __html: event.content || event.description }} />
                        )}

                        {hasLinks && (
                            <div className="t26-event-page__links">
                                {event.links.map((link, i) => {
                                    const url = link.url || link.href;
                                    const label = link.label || link.name || url;
                                    if (!url) return null;
                                    const fallback = isExternalUrl(url, org)
                                        ? { href: url, target: "_blank", rel: "noopener noreferrer" }
                                        : { href: buildLinkHref(resolveUrl(org, url), org) };
                                    return (
                                        <a key={i} className="t26-event-page__link" {...eventLinkProperties(url,context,fallback)}>
                                            <span>{label}</span>
                                            <b><Icon name="arrowRight" size={16} className="t26-flip-rtl" /></b>
                                        </a>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
                <EventAiContent event={event} context={context} className="t26-event-page__custom-content" />
            </div>
        </>
    );
}
